// StripeProvider.tsx
// Purpose: Provides Stripe SDK initialization for web payment processing
//
// This provider wraps the app with Elements from @stripe/react-stripe-js
// to enable payment method collection, checkout, and saved payment management.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { devLog, devWarn } from '../utils/devLog';

interface StripeContextType {
  isStripeReady: boolean;
  stripePromise: Promise<Stripe | null> | null;
  error: string | null;
  refreshStripeConfig: () => Promise<void>;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

// Get Stripe publishable key from environment
const getStripePublishableKey = (): string => {
  // Check React app environment variable
  // ⚠️ IMPORTANT: React apps bundle env vars at BUILD TIME, not runtime!
  // If your build used a test key, it's baked into the bundle even if Cloudflare has the prod key.
  const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (envKey) {
    // Detect if test key is being used in production
    const isProduction = window.location.hostname !== 'localhost' && 
                        window.location.hostname !== '127.0.0.1' &&
                        !window.location.hostname.includes('localhost') &&
                        !window.location.hostname.includes('127.0.0.1');
    
    // Log the key prefix for debugging (first 12 chars: pk_test_xxxx or pk_live_xxxx)
    const keyPrefix = envKey.substring(0, 12);
    devLog('[StripeProvider] Using Stripe key:', keyPrefix + '...', isProduction ? '(PRODUCTION)' : '(DEVELOPMENT)');
    
    if (isProduction && envKey.startsWith('pk_test_')) {
      const errorMsg = '🚨 CRITICAL: Test Stripe key detected in production! Using pk_test_ in production will show test accounts.';
      const fixMsg = 'FIX: Rebuild your app with REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_live_... set at BUILD TIME. Cloudflare env vars won\'t work - React bundles env vars during build.';
      console.error('[StripeProvider]', errorMsg);
      console.error('[StripeProvider]', fixMsg);
      console.error('[StripeProvider] Current key:', keyPrefix + '...');
      // Still return the key to prevent app crash, but log the error
    } else if (!isProduction && envKey.startsWith('pk_live_')) {
      devWarn('[StripeProvider] Live Stripe key detected in development. This may cause issues with test accounts.');
    }
    
    return envKey;
  }

  // Fallback - should be configured in environment
  console.error('[StripeProvider] No Stripe publishable key found! REACT_APP_STRIPE_PUBLISHABLE_KEY is not set.');
  return '';
};

interface StripeProviderProps {
  children: React.ReactNode;
}

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [isStripeReady, setIsStripeReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize Stripe on mount
  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const key = getStripePublishableKey();

        if (!key) {
          setError('Stripe publishable key not configured');
          setIsStripeReady(false);
          return;
        }

        // Load Stripe asynchronously
        const promise = loadStripe(key);
        setStripePromise(promise);

        // Verify Stripe loaded successfully
        const stripe = await promise;
        if (stripe) {
          setIsStripeReady(true);
          setError(null);
        } else {
          setError('Failed to load Stripe');
          setIsStripeReady(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize Stripe');
        setIsStripeReady(false);
      }
    };

    initializeStripe();
  }, []);

  // Refresh Stripe configuration
  const refreshStripeConfig = useCallback(async () => {
    try {
      const key = getStripePublishableKey();
      if (key) {
        const promise = loadStripe(key);
        setStripePromise(promise);

        const stripe = await promise;
        if (stripe) {
          setIsStripeReady(true);
          setError(null);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh Stripe config');
    }
  }, []);

  const contextValue: StripeContextType = {
    isStripeReady,
    stripePromise,
    error,
    refreshStripeConfig,
  };

  // If no Stripe promise, still render children but without Elements wrapper
  if (!stripePromise) {
    return <StripeContext.Provider value={contextValue}>{children}</StripeContext.Provider>;
  }

  return (
    <StripeContext.Provider value={contextValue}>
      <Elements
        stripe={stripePromise}
        options={{
          // Default appearance options for Stripe Elements
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#0066FF',
              colorBackground: '#ffffff',
              colorText: '#1a1a1a',
              colorDanger: '#df1b41',
              fontFamily: 'Inter, system-ui, sans-serif',
              spacingUnit: '4px',
              borderRadius: '8px',
            },
          },
        }}
      >
        {children}
      </Elements>
    </StripeContext.Provider>
  );
};

export const useStripeContext = (): StripeContextType => {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripeContext must be used within a StripeProvider');
  }
  return context;
};

export default StripeProvider;









